import { type TFunction } from 'i18next';

import { DEFAULT_NS } from '@/providers/TranslationProvider/constants';
import * as ApiModels from '@/typings/api-models';

type TranslatablePrice = Pick<ApiModels.Price, 'price' | 'currency' | 'recurringInterval' | 'sessionDuration'>;

export const numericPrice = (t: TFunction, priceModel: TranslatablePrice): string =>
  t('priceWithCurrency', {
    formatParams: { price: { currency: priceModel.currency } },
    ns: DEFAULT_NS,
    price: priceModel.price,
  });

export const price = (t: TFunction, priceModel?: TranslatablePrice, allowFree: boolean = false): string => {
  if (!priceModel || (allowFree && priceModel.price === 0)) {
    return t('priceWithCurrency--FREE', { ns: DEFAULT_NS });
  }
  return numericPrice(t, priceModel);
};

const intervalIsTranslatable = (intervalModel?: string | null): intervalModel is ApiModels.Interval =>
  !!(intervalModel && Object.values(ApiModels.Interval).some((apiInterval) => apiInterval === intervalModel));

export const recurring = (t: TFunction, priceModel?: TranslatablePrice, allowFree?: boolean): string => {
  if (!priceModel) {
    return '';
  }
  return intervalIsTranslatable(priceModel.recurringInterval)
    ? t('priceInterval', {
        interval: t(`interval--narrow--${priceModel.recurringInterval}`),
        ns: DEFAULT_NS,
        price: price(t, priceModel, allowFree),
      })
    : price(t, priceModel, allowFree);
};

const ALL_DAY_LIMIT = 8 * 60;

export const sessionDuration = (t: TFunction, priceModel?: TranslatablePrice): string => {
  if (!priceModel || !priceModel.sessionDuration) {
    return t('sessionDuration--NONE');
  }
  if (priceModel.sessionDuration >= ALL_DAY_LIMIT) {
    return t('sessionDuration--DAY');
  }
  return t('durationInMinutes', { count: priceModel.sessionDuration });
};
